import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRotateLeft, SearchNormal1 } from "iconsax-react";
import { Input, Checkbox, DatePicker, RangeSlider } from "@/shared/ui";
import type { RangeValue } from "@/shared/ui";
import type { Locale } from "@/shared/utils";
import { useGetRegionsQuery } from "@/shared/api/common";
import type { ProjectCategory, ProjectStatus } from "../../../types";
import { PROJECT_CATEGORIES, PROJECT_STATUS_MAP, CATEGORY_MAP } from "../../../utils";
import FilterSection from "./FilterSection";

function formatBudgetInput(value: string): string {
  const digits = value.replace(/\D/g, "");
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

function parseBudgetInput(formatted: string): string {
  return formatted.replace(/\s/g, "");
}

interface Props {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  selectedCategories: Set<ProjectCategory>;
  onCategoryToggle: (category: ProjectCategory) => void;
  selectedRegions: Set<number>;
  onRegionToggle: (regionId: number) => void;
  selectedStatuses: Set<ProjectStatus>;
  onStatusToggle: (status: ProjectStatus) => void;
  dateRange: RangeValue | null;
  onDateRangeChange: (value: RangeValue | null) => void;
  budgetFrom: string;
  onBudgetFromChange: (value: string) => void;
  budgetTo: string;
  onBudgetToChange: (value: string) => void;
  maxBudget: number;
  onReset: () => void;
  hasActiveFilters: boolean;
}

const VISIBLE_LIMIT = 3;
const PROJECT_STATUSES: ProjectStatus[] = ["planned", "active", "paused", "closed", "archived"];

export default function ProjectsFilterPanel({
  searchQuery,
  onSearchChange,
  selectedCategories,
  onCategoryToggle,
  selectedRegions,
  onRegionToggle,
  selectedStatuses,
  onStatusToggle,
  dateRange,
  onDateRangeChange,
  budgetFrom,
  onBudgetFromChange,
  budgetTo,
  onBudgetToChange,
  maxBudget,
  onReset,
  hasActiveFilters,
}: Props) {
  const { t, i18n } = useTranslation("ProjectsPage");
  const locale = (i18n.language as Locale) || "ru";
  const { data: regions } = useGetRegionsQuery();

  const [showAllCategories, setShowAllCategories] = useState(false);
  const [showAllRegions, setShowAllRegions] = useState(false);

  const visibleCategories = showAllCategories
    ? PROJECT_CATEGORIES.filter((c) => c !== "")
    : PROJECT_CATEGORIES.filter((c) => c !== "").slice(0, VISIBLE_LIMIT);

  const regionList = regions ?? [];
  const visibleRegions = showAllRegions ? regionList : regionList.slice(0, VISIBLE_LIMIT);

  function getRegionName(region: { name_ru: string; name_kk: string; name_en: string }): string {
    if (locale === "kk") return region.name_kk;
    if (locale === "en") return region.name_en;
    return region.name_ru;
  }

  return (
    <aside className="w-2xs shrink-0 flex flex-col radius-lg surface-base-fill overflow-hidden self-start">
      <div className="border-b surface-container-stroke px-5 py-4 flex items-center justify-between">
        <p className="text-label-lg content-base-primary">{t("filterPanel.filtersTitle")}</p>
        {hasActiveFilters && (
          <button
            type="button"
            className="flex justify-end items-center gap-1 text-label-sm content-base-secondary cursor-pointer"
            onClick={onReset}>
            {t("filterPanel.resetAll")}
            <ArrowRotateLeft size={16} color="currentColor" />
          </button>
        )}
      </div>

      <div className="px-5 py-4">
        <Input
          placeholder={t("filterPanel.searchPlaceholder")}
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          icon={<SearchNormal1 size={16} color="currentColor" />}
        />
      </div>

      <div className="border-t surface-container-stroke px-5 py-4">
        <FilterSection title={t("filterPanel.category")}>
          <div className="flex flex-col gap-1">
            {visibleCategories.map((cat) => (
              <Checkbox
                key={cat}
                state={selectedCategories.has(cat) ? "checked" : "unchecked"}
                onChange={() => onCategoryToggle(cat)}
                label={t(CATEGORY_MAP[cat].label)}
              />
            ))}
          </div>
          {PROJECT_CATEGORIES.filter((c) => c !== "").length > VISIBLE_LIMIT && (
            <button
              type="button"
              className="text-body-bold-sm content-action-brand cursor-pointer text-left"
              onClick={() => setShowAllCategories(!showAllCategories)}>
              {showAllCategories ? t("filterPanel.showLess") : t("filterPanel.showMore")}
            </button>
          )}
        </FilterSection>
      </div>

      <div className="border-t surface-container-stroke px-5 py-4">
        <FilterSection title={t("filterPanel.region")}>
          <div className="flex flex-col gap-1">
            {visibleRegions.map((region) => (
              <Checkbox
                key={region.id}
                state={selectedRegions.has(region.id) ? "checked" : "unchecked"}
                onChange={() => onRegionToggle(region.id)}
                label={getRegionName(region)}
              />
            ))}
          </div>
          {regionList.length > VISIBLE_LIMIT && (
            <button
              type="button"
              className="text-body-bold-sm content-action-brand cursor-pointer text-left"
              onClick={() => setShowAllRegions(!showAllRegions)}>
              {showAllRegions ? t("filterPanel.showLess") : t("filterPanel.showMore")}
            </button>
          )}
        </FilterSection>
      </div>

      <div className="border-t surface-container-stroke px-5 py-4">
        <FilterSection title={t("filterPanel.budget")}>
          {maxBudget > 0 && (
            <RangeSlider
              min={0}
              max={maxBudget}
              valueFrom={budgetFrom ? parseFloat(budgetFrom) : 0}
              valueTo={budgetTo ? parseFloat(budgetTo) : maxBudget}
              onChange={(from, to) => {
                onBudgetFromChange(from === 0 ? "" : String(from));
                onBudgetToChange(to === maxBudget ? "" : String(to));
              }}
            />
          )}
          <div className="flex items-center gap-2">
            <Input
              placeholder="0"
              value={budgetFrom ? formatBudgetInput(budgetFrom) : ""}
              onChange={(e) => {
                const raw = parseBudgetInput(e.target.value);
                onBudgetFromChange(raw);
              }}
            />
            <Input
              placeholder={formatBudgetInput(String(maxBudget))}
              value={budgetTo ? formatBudgetInput(budgetTo) : ""}
              onChange={(e) => {
                const raw = parseBudgetInput(e.target.value);
                onBudgetToChange(raw);
              }}
            />
          </div>
        </FilterSection>
      </div>

      <div className="border-t surface-container-stroke px-5 py-4">
        <FilterSection title={t("filterPanel.status")}>
          <div className="flex flex-col gap-1">
            {PROJECT_STATUSES.map((status) => (
              <Checkbox
                key={status}
                state={selectedStatuses.has(status) ? "checked" : "unchecked"}
                onChange={() => onStatusToggle(status)}
                label={t(PROJECT_STATUS_MAP[status].label)}
              />
            ))}
          </div>
        </FilterSection>
      </div>

      <div className="border-t surface-container-stroke px-5 py-4">
        <FilterSection title={t("filterPanel.dates")}>
          <DatePicker
            mode="range"
            value={dateRange}
            onChange={onDateRangeChange}
            placeholder={t("filterPanel.datesFrom")}
            locale={locale}
          />
        </FilterSection>
      </div>

    </aside>
  );
}
